import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER, DEFAULT_THEME_COLOR} from '../../constants/theme';

// Building blocks of the Facebook-like composers: freak, place and hub editors

export const COMPOSER_COLORS = {
  photo: '#45bd62',
  people: '#1877f2',
  location: '#f5533d',
  hub: '#F97300',
  route: DEFAULT_THEME_COLOR,
  date: '#a259ff',
  links: '#3ea8f3',
  freaky: '#e91e63',
};

export const composerText = {
  primary: 'rgba(255,255,255,0.92)',
  secondary: 'rgba(255,255,255,0.55)',
  border: 'rgba(255,255,255,0.1)',
  surface: 'rgba(255,255,255,0.06)',
};

// a rounded, clickable pill under the author's name (audience, date)
export const ComposerPill = ({ icon, label, onClick, testId }) => (
  <ButtonBase
    data-testid={testId}
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.25,
      mr: 0.75,
      mt: 0.5,
      borderRadius: 1.5,
      background: composerText.surface,
      color: composerText.secondary,
      fontSize: '0.8rem',
      fontWeight: 600,
      '& svg': { fontSize: 16 },
      '&:hover': { background: 'rgba(255,255,255,0.1)' },
    }}
  >
    {icon}
    <span>{label}</span>
    <ArrowDropDownIcon />
  </ButtonBase>
);

// a section for one tool (people, location, links…) with a coloured icon and a close button
export const ComposerPanel = ({ id, icon, color, title, onClose, children }) => (
  <Box
    id={id}
    data-testid={id}
    sx={{
      border: `1px solid ${composerText.border}`,
      borderRadius: 2,
      p: 2,
      mt: 2,
      scrollMarginTop: 80,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      <Box
        sx={{
          width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `${color}22`, color, mr: 1.5, '& svg': { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ flex: 1, color: composerText.primary, fontWeight: 600 }}>{title}</Typography>
      {onClose && (
        <IconButton size="small" onClick={onClose} aria-label="close" sx={{ color: composerText.secondary }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
    {children}
  </Box>
);

// "Add to your freak" — label plus one coloured icon per tool, like Facebook's composer
export const ComposerAddBar = ({ label, options }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 0.5,
      border: `1px solid ${composerText.border}`,
      borderRadius: 2,
      px: 2,
      py: 1,
      mt: 2,
    }}
  >
    <Typography sx={{ flex: 1, minWidth: 140, color: composerText.primary, fontWeight: 600 }}>{label}</Typography>
    {options.map(option => (
      <Tooltip key={option.key} title={option.label}>
        <IconButton
          data-testid={`composer-option-${option.key}`}
          aria-label={option.label}
          aria-pressed={!!option.active}
          onClick={option.onClick}
          sx={{
            color: option.color,
            background: option.active ? `${option.color}22` : 'transparent',
            '&:hover': { background: `${option.color}22` },
          }}
        >
          {option.icon}
        </IconButton>
      </Tooltip>
    ))}
  </Box>
);

// Facebook-style photo collage: 1 big, 2 side by side, 3+ one big and a column, "+N" on the last tile
export const ComposerMediaGrid = ({ images, onRemove }) => {
  const visible = images.slice(0, 4);
  const hidden = images.length - visible.length;

  const tile = (image, index, sx) => (
    <Box
      key={image.id || image.name || index}
      sx={{ position: 'relative', overflow: 'hidden', background: 'rgba(0,0,0,0.3)', ...sx }}
    >
      <Box component="img" src={image.imageUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      {hidden > 0 && index === visible.length - 1 && (
        <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>+{hidden}</Typography>
        </Box>
      )}
      <IconButton
        data-testid={`composer-remove-image-${index}`}
        aria-label="remove"
        size="small"
        onClick={() => onRemove(image)}
        sx={{ position: 'absolute', top: 6, right: 6, color: 'white', background: 'rgba(0,0,0,0.55)', '&:hover': { background: 'rgba(0,0,0,0.75)' } }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const gridSx = { display: 'flex', gap: '2px', mt: 2, borderRadius: 2, overflow: 'hidden' };
  if (visible.length === 1) {
    return <Box sx={gridSx}>{tile(visible[0], 0, { flex: 1, height: 340 })}</Box>;
  }
  if (visible.length === 2) {
    return <Box sx={gridSx}>{visible.map((image, i) => tile(image, i, { flex: 1, height: 260 }))}</Box>;
  }
  return (
    <Box sx={{ ...gridSx, height: 340 }}>
      {tile(visible[0], 0, { flex: 2 })}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {visible.slice(1).map((image, i) => tile(image, i + 1, { flex: 1 }))}
      </Box>
    </Box>
  );
};

export const postButtonSx = {
  background: DEFAULT_GRADIENT,
  color: 'white',
  fontWeight: 700,
  px: 3,
  flexShrink: 0,
  '&:hover': { background: DEFAULT_GRADIENT_HOVER },
  '&.Mui-disabled': { background: 'rgba(54,201,162,0.15)', color: 'rgba(255,255,255,0.3)' },
};

// the page around a composer: sticky back · title · action header, one centered card,
// a hint + full-width action under the card and a "discard?" dialog for unsaved changes
export const ComposerShell = ({
  title, actionLabel, canSubmit, onSubmit, isDirty, onLeave, leftNavigationOpen,
  errors, hint, texts, cardSx, children,
}) => {
  const isDesktop = useMediaQuery({ minWidth: 992 });
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const paddingLeft = (isDesktop && leftNavigationOpen) ? 300 : (isDesktop && !leftNavigationOpen ? 75 : 0);

  const goBack = () => {
    if (isDirty) {
      setIsDiscardOpen(true);
      return;
    }
    onLeave();
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)', paddingLeft: `${paddingLeft}px` }}>
      <Box
        sx={{
          position: 'sticky', top: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 1.5, sm: 3 }, py: 1.5, background: 'rgba(13,27,42,0.92)', backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <IconButton
          data-testid="composer-back"
          onClick={goBack}
          size="small"
          aria-label={texts.discard}
          sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" noWrap sx={{ color: 'white', fontWeight: 700, fontSize: '1.05rem' }}>{title}</Typography>
        <Button data-testid="composer-post" variant="contained" disabled={!canSubmit} onClick={onSubmit} sx={postButtonSx}>
          {actionLabel}
        </Button>
      </Box>

      <Box sx={{ maxWidth: 680, mx: 'auto', px: { xs: 1.5, sm: 3 }, py: 3 }}>
        {errors}
        <Box
          sx={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3,
            p: { xs: 2, sm: 3 }, ...cardSx,
          }}
        >
          {children}
          {hint && (
            <Typography variant="body2" sx={{ color: composerText.secondary, textAlign: 'center', mt: 2 }}>{hint}</Typography>
          )}
          <Button fullWidth variant="contained" disabled={!canSubmit} onClick={onSubmit} sx={{ ...postButtonSx, mt: 2, py: 1.2 }}>
            {actionLabel}
          </Button>
        </Box>
        <Box sx={{ pb: 6 }} />
      </Box>

      <Dialog open={isDiscardOpen} onClose={() => setIsDiscardOpen(false)}>
        <DialogTitle>{texts.discardTitle}</DialogTitle>
        <DialogContent>
          <Typography>{texts.discardMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDiscardOpen(false)}>{texts.keepEditing}</Button>
          <Button data-testid="composer-discard" color="error" onClick={onLeave}>{texts.discard}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// a big, borderless title input like the first line of a post
export const ComposerTitleInput = ({ value, onChange, placeholder, testId, sx }) => (
  <InputBase
    fullWidth
    multiline
    inputProps={{ 'data-testid': testId, maxLength: 200 }}
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={{ color: composerText.primary, fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, ...sx }}
  />
);

// a one-line note under the title (who can see it)
export const ComposerInfoLine = ({ icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, color: composerText.secondary, '& svg': { fontSize: 16 } }}>
    {icon}
    <Typography variant="body2" sx={{ color: composerText.secondary }}>{children}</Typography>
  </Box>
);
